import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "video-session-page" }
const _hoisted_2 = {
  key: 0,
  class: "state-screen"
}
const _hoisted_3 = {
  key: 1,
  class: "state-screen"
}
const _hoisted_4 = { class: "text-medium-emphasis mt-1" }
const _hoisted_5 = {
  key: 2,
  class: "state-screen"
}
const _hoisted_6 = { class: "text-medium-emphasis mt-2 text-center" }
const _hoisted_7 = { class: "countdown mt-4" }
const _hoisted_8 = { class: "countdown__time" }
const _hoisted_9 = {
  key: 3,
  class: "state-screen"
}
const _hoisted_10 = {
  key: 4,
  class: "lobby"
}
const _hoisted_11 = { class: "lobby-card" }
const _hoisted_12 = { class: "font-size-18-600 mb-1" }
const _hoisted_13 = {
  key: 0,
  class: "text-medium-emphasis text-sm mb-4"
}
const _hoisted_14 = { class: "preview-placeholder" }
const _hoisted_15 = {
  width: "32",
  height: "32",
  viewBox: "0 0 24 24",
  fill: "currentColor",
  style: {"color":"rgba(255,255,255,0.5)"}
}
const _hoisted_16 = {
  key: 1,
  class: "text-error mb-3"
}
const _hoisted_17 = {
  key: 5,
  class: "room-layout"
}
const _hoisted_18 = { class: "participant-label" }
const _hoisted_19 = {
  key: 0,
  class: "waiting-tile"
}
const _hoisted_20 = {
  width: "48",
  height: "48",
  viewBox: "0 0 24 24",
  fill: "currentColor",
  style: {"color":"rgba(255,255,255,0.4)"}
}
const _hoisted_21 = { class: "toolbar" }
const _hoisted_22 = ["title"]
const _hoisted_23 = {
  key: 0,
  width: "24",
  height: "24",
  viewBox: "0 0 24 24",
  fill: "currentColor"
}
const _hoisted_24 = {
  key: 1,
  width: "24",
  height: "24",
  viewBox: "0 0 24 24",
  fill: "currentColor"
}
const _hoisted_25 = ["title"]
const _hoisted_26 = {
  key: 0,
  width: "24",
  height: "24",
  viewBox: "0 0 24 24",
  fill: "currentColor"
}
const _hoisted_27 = {
  key: 1,
  width: "24",
  height: "24",
  viewBox: "0 0 24 24",
  fill: "currentColor"
}
const _hoisted_28 = {
  key: 6,
  class: "state-screen"
}

import { ref, reactive, onMounted, onUnmounted, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { connect, LocalVideoTrack } from 'twilio-video';
import { VideoService } from '@/api/services/video';
import moment from 'moment';

type GateStatus = 'loading' | 'too-early' | 'open' | 'too-late';


export default /*@__PURE__*/_defineComponent({
  __name: 'VideoSessionView',
  setup(__props) {

const route = useRoute();
const router = useRouter();

const selfVideoContainer = ref<HTMLElement | null>(null);
const previewContainer = ref<HTMLElement | null>(null);
const participantRefs = ref<Record<string, HTMLElement>>({});

const eventId = Number(route.params.eventId);
const videoService = new VideoService(eventId);

const state = reactive({
  loading: true,
  joining: false,
  fatalError: null as string | null,
  error: null as string | null,
  event: null as any,
  windowOpens: null as string | null,
  windowCloses: null as string | null,
  gateStatus: 'loading' as GateStatus,
  isConnected: false,
  ended: false,
  participants: [] as { identity: string }[],
  isMuted: false,
  isVideoOff: false,
  countdown: '',
});

let room: any = null;
let countdownTimer: ReturnType<typeof setInterval> | null = null;
let previewTrack: any = null;

// ─────────────────────────────────────────────────────────────
// Lifecycle
// ─────────────────────────────────────────────────────────────

onMounted(async () => {
  await loadEvent();
});

onUnmounted(() => {
  if (room) room.disconnect();
  if (countdownTimer) clearInterval(countdownTimer);
  if (previewTrack) {
    previewTrack.stop();
    previewTrack.detach().forEach((el: HTMLElement) => el.remove());
  }
});

// ─────────────────────────────────────────────────────────────
// Load event & evaluate time gate
// ─────────────────────────────────────────────────────────────

async function loadEvent() {
  state.loading = true;

  try {
    const res = await videoService.getEvent();
    const data = res.data?.data ?? res.data;
    state.event = data;
    state.windowOpens = data.window_opens ?? null;
    state.windowCloses = data.window_closes ?? null;

    evaluateGate();
    startCountdownIfNeeded();
  } catch (e: any) {
    state.fatalError = e?.response?.data?.error ?? 'Failed to load session details.';
  } finally {
    state.loading = false;
  }

  // Must run after loading=false so the lobby DOM (previewContainer ref) has rendered
  await startPreview();
}

function evaluateGate() {
  if (!state.windowOpens || !state.windowCloses) {
    state.gateStatus = 'open';
    return;
  }

  const now = Date.now();
  const opens = new Date(state.windowOpens).getTime();
  const closes = new Date(state.windowCloses).getTime();

  if (now < opens) {
    state.gateStatus = 'too-early';
  } else if (now > closes) {
    state.gateStatus = 'too-late';
  } else {
    state.gateStatus = 'open';
  }
}

function startCountdownIfNeeded() {
  if (state.gateStatus !== 'too-early' || !state.windowOpens) return;

  const tick = () => {
    const diff = new Date(state.windowOpens!).getTime() - Date.now();
    if (diff <= 0) {
      state.gateStatus = 'open';
      clearInterval(countdownTimer!);
      countdownTimer = null;
      startPreview();
      return;
    }
    const m = Math.floor(diff / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    state.countdown = `${m}m ${s.toString().padStart(2, '0')}s`;
  };

  tick();
  countdownTimer = setInterval(tick, 1000);
}

// ─────────────────────────────────────────────────────────────
// Camera preview in lobby
// ─────────────────────────────────────────────────────────────

async function startPreview() {
  if (state.gateStatus !== 'open') return;

  await nextTick();
  if (!previewContainer.value) return;

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    const track = stream.getVideoTracks()[0];
    const video = document.createElement('video');
    video.srcObject = stream;
    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;
    video.style.cssText = 'width:100%;height:100%;object-fit:cover;border-radius:12px;';
    previewContainer.value.innerHTML = '';
    previewContainer.value.appendChild(video);
    previewTrack = { stop: () => track.stop(), detach: () => [] };
  } catch {
    // Camera access denied — preview placeholder stays
  }
}

// ─────────────────────────────────────────────────────────────
// Room management
// ─────────────────────────────────────────────────────────────

async function joinRoom() {
  state.joining = true;
  state.error = null;

  // Stop preview stream before connecting
  if (previewTrack) {
    previewTrack.stop();
    previewTrack = null;
    if (previewContainer.value) previewContainer.value.innerHTML = '';
  }

  try {
    const res = await videoService.getToken();
    const { token, room_name } = res.data ?? res;

    room = await connect(token, {
      name: room_name,
      audio: true,
      video: { width: 1280, height: 720 },
    });

    // Set connected first so Vue renders the room layout (incl. selfVideoContainer)
    state.isConnected = true;
    await nextTick();

    room.localParticipant.tracks.forEach((pub: any) => {
      if (pub.track && selfVideoContainer.value) {
        selfVideoContainer.value.innerHTML = '';
        const el = pub.track.attach();
        el.style.cssText = 'width:100%;height:100%;object-fit:cover;border-radius:8px;';
        selfVideoContainer.value.appendChild(el);
      }
    });

    room.participants.forEach(onParticipantConnected);
    room.on('participantConnected', onParticipantConnected);
    room.on('participantDisconnected', onParticipantDisconnected);
    room.on('disconnected', onRoomDisconnected);
  } catch (e: any) {
    state.error = e?.message ?? 'Failed to join. Please check your camera and microphone permissions.';
  } finally {
    state.joining = false;
  }
}

function setParticipantRef(el: HTMLElement | null, identity: string) {
  if (el) participantRefs.value[identity] = el;
}

function attachTrack(track: any, identity: string) {
  nextTick(() => {
    const wrapper = participantRefs.value[identity];
    if (!wrapper) return;
    if (track.kind === 'video') {
      wrapper.querySelector('video')?.remove();
      const el = track.attach();
      el.style.cssText = 'width:100%;height:100%;object-fit:cover;';
      wrapper.appendChild(el);
    }
  });
}

function onParticipantConnected(participant: any) {
  if (!state.participants.find(p => p.identity === participant.identity)) {
    state.participants.push({ identity: participant.identity });
  }
  participant.tracks.forEach((pub: any) => {
    if (pub.isSubscribed && pub.track) attachTrack(pub.track, participant.identity);
  });
  participant.on('trackSubscribed', (track: any) => attachTrack(track, participant.identity));
  participant.on('trackUnsubscribed', (track: any) => track.detach().forEach((el: HTMLElement) => el.remove()));
}

function onParticipantDisconnected(participant: any) {
  state.participants = state.participants.filter(p => p.identity !== participant.identity);
}

function onRoomDisconnected() {
  state.isConnected = false;
  state.ended = true;
  state.participants = [];
  participantRefs.value = {};
}

function leaveRoom() {
  if (room) room.disconnect();
}

function toggleMute() {
  if (!room) return;
  state.isMuted = !state.isMuted;
  room.localParticipant.audioTracks.forEach(({ track }: any) => track.enable(!state.isMuted));
}

function toggleVideo() {
  if (!room) return;
  state.isVideoOff = !state.isVideoOff;
  room.localParticipant.videoTracks.forEach(({ track }: any) => track.enable(!state.isVideoOff));
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

function formatDate(dateStr: string) {
  return moment(dateStr).format('ddd D MMM YYYY, h:mm a');
}

return (_ctx: any,_cache: any) => {
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_btn = _resolveComponent("v-btn")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (state.loading)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(_component_v_progress_circular, {
            indeterminate: "",
            color: "primary",
            size: "64"
          }),
          _cache[4] || (_cache[4] = _createElementVNode("p", { class: "mt-4 text-medium-emphasis" }, "Loading your session…", -1))
        ]))
      : (state.fatalError)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _cache[6] || (_cache[6] = _createElementVNode("svg", {
              width: "64",
              height: "64",
              viewBox: "0 0 24 24",
              fill: "currentColor",
              class: "icon-error"
            }, [
              _createElementVNode("path", { d: "M11 15h2v2h-2v-2zm0-8h2v6h-2V7zm1-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" })
            ], -1)),
            _cache[7] || (_cache[7] = _createElementVNode("h3", { class: "mt-3" }, "Unable to load session", -1)),
            _createElementVNode("p", _hoisted_4, _toDisplayString(state.fatalError), 1),
            _createVNode(_component_v_btn, {
              class: "mt-4",
              color: "primary",
              variant: "outlined",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$router.back()))
            }, {
              default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                _createTextVNode("Go back", -1)
              ]))]),
              _: 1
            })
          ]))
        : (state.gateStatus === 'too-early')
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _cache[12] || (_cache[12] = _createElementVNode("svg", {
                width: "64",
                height: "64",
                viewBox: "0 0 24 24",
                fill: "currentColor",
                class: "icon-primary"
              }, [
                _createElementVNode("path", { d: "M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23L12.5 13V7z" })
              ], -1)),
              _cache[13] || (_cache[13] = _createElementVNode("h3", { class: "mt-3 font-size-18-600" }, "Your session isn't open yet", -1)),
              _createElementVNode("p", _hoisted_6, [
                _cache[8] || (_cache[8] = _createTextVNode(" This video room opens ", -1)),
                _createElementVNode("strong", null, _toDisplayString(formatDate(state.windowOpens)), 1),
                _cache[9] || (_cache[9] = _createTextVNode(" (5 minutes before your appointment). ", -1))
              ]),
              _createElementVNode("div", _hoisted_7, [
                _cache[10] || (_cache[10] = _createElementVNode("span", { class: "countdown__label" }, "Opens in", -1)),
                _createElementVNode("span", _hoisted_8, _toDisplayString(state.countdown), 1)
              ]),
              _createVNode(_component_v_btn, {
                class: "mt-6",
                color: "primary",
                variant: "outlined",
                onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$router.back()))
              }, {
                default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                  _createTextVNode("Go back", -1)
                ]))]),
                _: 1
              })
            ]))
          : (state.gateStatus === 'too-late')
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                _cache[15] || (_cache[15] = _createElementVNode("svg", {
                  width: "64",
                  height: "64",
                  viewBox: "0 0 24 24",
                  fill: "currentColor",
                  class: "icon-warning"
                }, [
                  _createElementVNode("path", { d: "M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-.5-5h1v2h-1v-2zm0-8h1v6h-1V7z" })
                ], -1)),
                _cache[16] || (_cache[16] = _createElementVNode("h3", { class: "mt-3 font-size-18-600" }, "This session has ended", -1)),
                _cache[17] || (_cache[17] = _createElementVNode("p", { class: "text-medium-emphasis mt-2 text-center" }, " The video room for this appointment is no longer available. ", -1)),
                _createVNode(_component_v_btn, {
                  class: "mt-6",
                  color: "primary",
                  variant: "outlined",
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$router.back()))
                }, {
                  default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                    _createTextVNode("Go back", -1)
                  ]))]),
                  _: 1
                })
              ]))
            : (state.gateStatus === 'open' && !state.isConnected)
              ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                  _createElementVNode("div", _hoisted_11, [
                    _cache[22] || (_cache[22] = _createElementVNode("svg", {
                      width: "56",
                      height: "56",
                      viewBox: "0 0 24 24",
                      fill: "currentColor",
                      class: "icon-primary mb-3"
                    }, [
                      _createElementVNode("path", { d: "M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" })
                    ], -1)),
                    _createElementVNode("h2", _hoisted_12, _toDisplayString(state.event?.name ?? 'Video Session'), 1),
                    (state.event)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_13, _toDisplayString(formatDate(state.event.start_date)), 1))
                      : _createCommentVNode("", true),
                    _createElementVNode("div", {
                      class: "preview-container mb-4",
                      ref_key: "previewContainer",
                      ref: previewContainer
                    }, [
                      _createElementVNode("div", _hoisted_14, [
                        (_openBlock(), _createElementBlock("svg", _hoisted_15, [...(_cache[18] || (_cache[18] = [
                          _createElementVNode("path", { d: "M12 15.2A3.2 3.2 0 0 1 8.8 12 3.2 3.2 0 0 1 12 8.8 3.2 3.2 0 0 1 15.2 12 3.2 3.2 0 0 1 12 15.2M9 2 7.17 4H4C2.9 4 2 4.9 2 6v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9z" }, null, -1)
                        ]))])),
                        _cache[19] || (_cache[19] = _createElementVNode("p", {
                          class: "mt-1",
                          style: {"font-size":"12px","color":"#aaa"}
                        }, "Camera preview", -1))
                      ])
                    ], 512),
                    (state.error)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_16, _toDisplayString(state.error), 1))
                      : _createCommentVNode("", true),
                    _createVNode(_component_v_btn, {
                      color: "primary",
                      size: "large",
                      rounded: "",
                      loading: state.joining,
                      onClick: joinRoom
                    }, {
                      prepend: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                        _createElementVNode("svg", {
                          width: "20",
                          height: "20",
                          viewBox: "0 0 24 24",
                          fill: "currentColor"
                        }, [
                          _createElementVNode("path", { d: "M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" })
                        ], -1)
                      ]))]),
                      default: _withCtx(() => [
                        _cache[21] || (_cache[21] = _createTextVNode(" Join Session ", -1))
                      ]),
                      _: 1
                    }, 8, ["loading"])
                  ])
                ]))
              : (state.isConnected)
                ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                    _createElementVNode("div", {
                      class: _normalizeClass(["participants-grid", { 'single': state.participants.length <= 1 }])
                    }, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(state.participants, (p) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: p.identity,
                          class: "participant-tile",
                          ref_for: true,
                          ref: el => setParticipantRef(el, p.identity)
                        }, [
                          _createElementVNode("div", _hoisted_18, _toDisplayString(p.identity), 1)
                        ]))
                      }), 128)),
                      (state.participants.length === 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                            (_openBlock(), _createElementBlock("svg", _hoisted_20, [...(_cache[23] || (_cache[23] = [
                              _createElementVNode("path", { d: "M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" }, null, -1)
                            ]))])),
                            _cache[24] || (_cache[24] = _createElementVNode("p", {
                              class: "mt-2",
                              style: {"color":"rgba(255,255,255,0.5)"}
                            }, "Waiting for your practitioner…", -1))
                          ]))
                        : _createCommentVNode("", true)
                    ], 2),
                    _createElementVNode("div", {
                      class: "self-pip",
                      ref_key: "selfVideoContainer",
                      ref: selfVideoContainer
                    }, [...(_cache[25] || (_cache[25] = [
                      _createElementVNode("div", { class: "self-label" }, "You", -1)
                    ]))], 512),
                    _createElementVNode("div", _hoisted_21, [
                      _createElementVNode("button", {
                        class: _normalizeClass(["ctrl-btn", { 'ctrl-btn--active': state.isMuted }]),
                        onClick: toggleMute,
                        title: state.isMuted ? 'Unmute' : 'Mute'
                      }, [
                        (!state.isMuted)
                          ? (_openBlock(), _createElementBlock("svg", _hoisted_23, [...(_cache[26] || (_cache[26] = [
                              _createElementVNode("path", { d: "M12 2a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3 3 3 0 0 1-3-3V5a3 3 0 0 1 3-3m7 9c0 3.53-2.61 6.44-6 6.93V21h-2v-3.07C7.61 17.44 5 14.53 5 11h2a5 5 0 0 0 5 5 5 5 0 0 0 5-5h2z" }, null, -1)
                            ]))]))
                          : (_openBlock(), _createElementBlock("svg", _hoisted_24, [...(_cache[27] || (_cache[27] = [
                              _createElementVNode("path", { d: "M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28m-4.02.17c0-.06.02-.11.02-.17V5a3 3 0 0 0-3-3 3 3 0 0 0-3 3v.18l5.98 5.99M4.27 3 3 4.27l6.01 6.01V11a3 3 0 0 0 3 3c.23 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z" }, null, -1)
                            ]))]))
                      ], 10, _hoisted_22),
                      _createElementVNode("button", {
                        class: _normalizeClass(["ctrl-btn", { 'ctrl-btn--active': state.isVideoOff }]),
                        onClick: toggleVideo,
                        title: state.isVideoOff ? 'Start video' : 'Stop video'
                      }, [
                        (!state.isVideoOff)
                          ? (_openBlock(), _createElementBlock("svg", _hoisted_26, [...(_cache[28] || (_cache[28] = [
                              _createElementVNode("path", { d: "M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" }, null, -1)
                            ]))]))
                          : (_openBlock(), _createElementBlock("svg", _hoisted_27, [...(_cache[29] || (_cache[29] = [
                              _createElementVNode("path", { d: "M21 6.5l-4 4V7c0-.55-.45-1-1-1H9.82L21 17.18V6.5zM3.27 2 2 3.27l4.73 4.73H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.54-.18L19.73 21 21 19.73 3.27 2z" }, null, -1)
                            ]))]))
                      ], 10, _hoisted_25),
                      _createElementVNode("button", {
                        class: "ctrl-btn ctrl-btn--hangup",
                        onClick: leaveRoom,
                        title: "Leave session"
                      }, [...(_cache[30] || (_cache[30] = [
                        _createElementVNode("svg", {
                          width: "24",
                          height: "24",
                          viewBox: "0 0 24 24",
                          fill: "currentColor"
                        }, [
                          _createElementVNode("path", {
                            d: "M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z",
                            transform: "rotate(135 12 12)"
                          })
                        ], -1)
                      ]))])
                    ])
                  ]))
                : (state.ended)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_28, [
                      _cache[32] || (_cache[32] = _createElementVNode("svg", {
                        width: "64",
                        height: "64",
                        viewBox: "0 0 24 24",
                        fill: "currentColor",
                        class: "icon-success"
                      }, [
                        _createElementVNode("path", { d: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm4.59-12.42L10 14.17l-2.59-2.58L6 13l4 4 8-8-1.41-1.42z" })
                      ], -1)),
                      _cache[33] || (_cache[33] = _createElementVNode("h3", { class: "mt-3" }, "Session ended", -1)),
                      _cache[34] || (_cache[34] = _createElementVNode("p", { class: "text-medium-emphasis mt-1" }, "Thank you for attending your appointment.", -1)),
                      _createVNode(_component_v_btn, {
                        class: "mt-6",
                        color: "primary",
                        variant: "outlined",
                        onClick: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$router.back()))
                      }, {
                        default: _withCtx(() => [...(_cache[31] || (_cache[31] = [
                          _createTextVNode("Back to appointments", -1)
                        ]))]),
                        _: 1
                      })
                    ]))
                  : _createCommentVNode("", true)
  ]))
}
}

})